<script setup lang="ts">
type Props = {
  selectedReward: Reward;
};

const { selectedReward } = defineProps<Props>();

const modelValue = defineModel<boolean>({
  required: true,
});

const isLoadingRedeem = ref(false);
const isRedeemSuccess = ref(false);

const { t } = useTexts();
const { redeemReward } = useRewards();
const { openToaster } = useLayoutStore();

async function handleRedeemReward() {
  if (!selectedReward) {
    return;
  }

  isLoadingRedeem.value = true;

  await redeemReward(selectedReward.id)
    .then((response) => {
      if (response?.data?.success) {
        isRedeemSuccess.value = true;
      }
    })
    .catch((error: any) => {
      const message = error?.data?.user_message || t('rewards.redeem_error');

      openToaster({
        message,
        type: 'error',
      });

      isRedeemSuccess.value = false;
    })
    .finally(() => {
      isLoadingRedeem.value = false;
    });
}

function handleCloseRedeemSuccess() {
  isRedeemSuccess.value = false;
  modelValue.value = false;
}
</script>

<template>
  <div :class="$style.modalRedeem">
    <BaseLoading
      class="!bg-auth-bg-primary"
      :show="isLoadingRedeem"
    />

    <UserRewardsRedeemSuccess
      v-if="isRedeemSuccess"
      :selected-reward="selectedReward"
      @close="handleCloseRedeemSuccess"
    />

    <template v-else>
      <p :class="$style.confirmText">
        <NuxtIcon
          :class="$style.confirmTextIcon"
          name="check"
        />

        <strong>
          {{ $t('rewards.redeem_confirm') }}
        </strong>
      </p>

      <div :class="$style.itemDetail">
        <UserRewardsItem
          :key="selectedReward?.id"
          :big-card="true"
          :button-visible="false"
          :reward="selectedReward"
          :status-visible="false"
        />
      </div>

      <div :class="$style.confirmationButtons">
        <button
          class="step-actions-btn step-actions-btn-true"
          @click="handleRedeemReward()"
        >
          {{ $t('general.confirm') }}
        </button>

        <button
          class="step-actions-btn step-actions-btn-false"
          @click="modelValue = false"
        >
          {{ $t('general.cancel') }}
        </button>
      </div>
    </template>
  </div>
</template>

<style module lang="postcss">
.modalRedeem {
  @apply text-center p-10;

  .itemDetail {
    @apply text-left w-full my-6;
  }

  .confirmText {
    @apply text-center flex gap-2 items-center justify-center;

    .confirmTextIcon {
      @apply text-xl text-success w-8 h-8 rounded-full bg-success/10 flex items-center justify-center;
    }

    strong {
      @apply text-2xl font-bold text-texts;
    }
  }

  .confirmationButtons {
    @apply grid grid-cols-2 gap-2;

    button {
      @apply py-2 rounded-md capitalize font-semibold;

      &:first-child {
        @apply bg-success text-white;
      }
      &:last-child {
        @apply bg-error text-white;
      }

      &:disabled {
        @apply opacity-40 cursor-not-allowed;
      }
    }
  }
}
</style>
